/**
 * Sidebar tool order from account.config.tools_order (see login/getConfig).
 * config.tools = enabled tools; tools_order = display order in the menu.
 */

const RADAR_TOOL_ID = 'navRadar'
const CHECKLIST_TOOL_ID = 'navChecklist'
const DIGITAL_LABEL_TOOL_ID = 'navDigitalLabel'
const ALLERGENS_TOOL_ID = 'navAllergens'
const LIBRARY_TOOL_ID = 'navLibrary'

export function getAccountToolsOrder(rootGetters) {
    const config = rootGetters['login/getConfig']
    if (!config || config.tools_order == null || config.tools_order === '') {
        return null
    }

    let toolsOrder = config.tools_order

    if (typeof toolsOrder === 'string') {
        try {
            toolsOrder = JSON.parse(toolsOrder)
        } catch {
            toolsOrder = toolsOrder
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean)
        }
    }

    if (!Array.isArray(toolsOrder) || toolsOrder.length === 0) {
        return null
    }

    return toolsOrder.map((code) => Number(code))
}

function resolveToolCode(tool) {
    if (tool.code === undefined || tool.code === null) {
        return null
    }

    return Number(tool.code)
}

function buildOrderIndexMap(toolsOrder) {
    const orderIndex = new Map()
    toolsOrder.forEach((code, index) => {
        orderIndex.set(Number(code), index)
    })
    return orderIndex
}

function compareByPriority(t1, t2) {
    const p1 = typeof t1.priority !== 'undefined' ? t1.priority : 9999
    const p2 = typeof t2.priority !== 'undefined' ? t2.priority : 9999

    if (p1 < p2) {
        return -1
    }
    if (p1 > p2) {
        return 1
    }
    return 0
}

/**
 * When the radar tool is enabled, force it to render right before the
 * checklist ("Tareas") tool, regardless of its configured priority/order.
 */
function placeRadarBeforeChecklist(tools) {
    const radarIndex = tools.findIndex((tool) => tool.id === RADAR_TOOL_ID)

    if (radarIndex === -1) {
        return tools
    }

    const list = [...tools]
    const [radarTool] = list.splice(radarIndex, 1)
    const checklistIndex = list.findIndex((tool) => tool.id === CHECKLIST_TOOL_ID)
    const insertIndex = checklistIndex === -1 ? 0 : checklistIndex

    list.splice(insertIndex, 0, radarTool)

    return list
}

/**
 * Force Alérgenos between Trazabilidad and Librería when present.
 */
function placeAllergensBetweenTraceabilityAndLibrary(tools) {
    const allergensIndex = tools.findIndex((tool) => tool.id === ALLERGENS_TOOL_ID)

    if (allergensIndex === -1) {
        return tools
    }

    const list = [...tools]
    const [allergensTool] = list.splice(allergensIndex, 1)
    const digitalLabelIndex = list.findIndex((tool) => tool.id === DIGITAL_LABEL_TOOL_ID)
    const libraryIndex = list.findIndex((tool) => tool.id === LIBRARY_TOOL_ID)

    let insertIndex
    if (digitalLabelIndex !== -1) {
        insertIndex = digitalLabelIndex + 1
    } else if (libraryIndex !== -1) {
        insertIndex = libraryIndex
    } else {
        insertIndex = list.length
    }

    list.splice(insertIndex, 0, allergensTool)

    return list
}

function sortByConfiguredOrder(list, rootGetters) {
    const toolsOrder = getAccountToolsOrder(rootGetters)

    if (!toolsOrder) {
        return list.sort(compareByPriority)
    }

    const orderIndex = buildOrderIndexMap(toolsOrder)
    const unknownOrderRank = orderIndex.size

    return list.sort((a, b) => {
        const codeA = resolveToolCode(a)
        const codeB = resolveToolCode(b)
        const indexA = codeA !== null && orderIndex.has(codeA) ? orderIndex.get(codeA) : unknownOrderRank
        const indexB = codeB !== null && orderIndex.has(codeB) ? orderIndex.get(codeB) : unknownOrderRank

        if (indexA !== indexB) {
            return indexA - indexB
        }

        return compareByPriority(a, b)
    })
}

export function sortSidebarTools(tools, rootGetters) {
    const list = Array.isArray(tools) ? [...tools] : Object.values(tools || {})
    let sorted = sortByConfiguredOrder(list, rootGetters)

    sorted = placeAllergensBetweenTraceabilityAndLibrary(sorted)

    if (rootGetters && rootGetters['getShowRadarTool']) {
        return placeRadarBeforeChecklist(sorted)
    }

    return sorted
}
